<template>
  <div class="supplier-statistics-container">
    <div class="statistics-card">
      <!-- 标题栏 -->
      <div class="card-header">
        <div class="header-title">
          <i class="el-icon-data-analysis"></i>
          <span>应付账款表</span>
        </div>
        <div class="header-toolbar">
          <el-date-picker
              size="small"
              v-model="timeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              @change="handleTimeChange"
              :picker-options="pickerOptions"
          />
          <el-button type="primary" size="small" @click="loadData" icon="el-icon-search">查询</el-button>
          <el-button type="success" size="small" @click="handleExport" icon="el-icon-download">导出</el-button>
        </div>
      </div>

      <!-- 统计汇总卡片 -->
      <div class="summary-cards">
        <div class="summary-card">
          <div class="summary-icon total-icon">
            <i class="el-icon-s-grid"></i>
          </div>
          <div class="summary-info">
            <div class="summary-label">供应商总数</div>
            <div class="summary-value">{{ supplierDataList.length }}</div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon inc-icon">
            <i class="el-icon-arrow-up"></i>
          </div>
          <div class="summary-info">
            <div class="summary-label">总增加金额</div>
            <div class="summary-value">{{ formatAmount(totalInc) }}<span class="unit">元</span></div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon reduce-icon">
            <i class="el-icon-arrow-down"></i>
          </div>
          <div class="summary-info">
            <div class="summary-label">总减少金额</div>
            <div class="summary-value">{{ formatAmount(totalReduce) }}<span class="unit">元</span></div>
          </div>
        </div>
        <div class="summary-card">
          <div class="summary-icon ending-icon">
            <i class="el-icon-s-data"></i>
          </div>
          <div class="summary-info">
            <div class="summary-label">总期末金额</div>
            <div class="summary-value">{{ formatAmount(totalEnding) }}<span class="unit">元</span></div>
          </div>
        </div>
      </div>

      <!-- 数据表格 -->
      <div class="data-table-wrapper" v-loading="loading">
        <el-table
            :data="tableData"
            border
            stripe
            style="width: 100%"
            :header-cell-style="{ background: '#f5f7fa', color: '#303133', fontWeight: '600' }"
        >
          <el-table-column
              prop="supplierName"
              label="供应商名称"
              min-width="150"
              fixed="left"
          >
            <template slot-scope="scope">
              <div class="supplier-name-cell">
                <i class="el-icon-s-shop"></i>
                <span>{{ scope.row.supplierName }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column
              prop="beginAmt"
              label="期初金额 (元)"
              min-width="140"
              align="right"
          >
            <template slot-scope="scope">
              <span class="amount-text begin-amount">
                {{ formatAmount(scope.row.beginAmt) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
              prop="issueInc"
              label="本期增加 (元)"
              min-width="140"
              align="right"
          >
            <template slot-scope="scope">
              <span class="amount-text inc-amount">
                + {{ formatAmount(scope.row.issueInc) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
              prop="issueReduce"
              label="本期减少 (元)"
              min-width="140"
              align="right"
          >
            <template slot-scope="scope">
              <span class="amount-text reduce-amount">
                - {{ formatAmount(scope.row.issueReduce) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
              prop="endingAmt"
              label="期末金额 (元)"
              min-width="140"
              align="right"
          >
            <template slot-scope="scope">
              <span class="amount-text ending-amount">
                {{ formatAmount(scope.row.endingAmt) }}
              </span>
            </template>
          </el-table-column>

          <el-table-column
              label="资金变动趋势"
              min-width="180"
              align="center"
          >
            <template slot-scope="scope">
              <div class="trend-indicator">
                <el-progress
                    :percentage="getTrendPercentage(scope.row)"
                    :color="getTrendColor(scope.row)"
                    :stroke-width="8"
                    :show-text="false"
                ></el-progress>
                <span class="trend-text" :style="{ color: getTrendColor(scope.row) }">
                  {{ getTrendText(scope.row) }}
                </span>
              </div>
            </template>
          </el-table-column>

          <!-- 操作列：详情按钮 -->
          <el-table-column
              label="操作"
              fixed="right"
              width="100"
              align="center"
          >
            <template slot-scope="scope">
              <el-button type="text" size="small" @click="showDetail(scope.row)">
                <i class="el-icon-view"></i> 详情
              </el-button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 图表展示区域 -->
<!--
      <div class="chart-container" v-if="showChart && supplierDataList.length > 0">
        <div class="chart-header">
          <span><i class="el-icon-data-line"></i> 供应商资金对比图</span>
        </div>
        <div ref="chartDom" style="height: 400px; width: 100%;"></div>
      </div>
-->
    </div>

    <!-- 供应商资金变动明细模态框 -->
    <el-dialog
        :title="`供应商资金变动明细 - ${detailSupplierName}`"
        :visible.sync="detailVisible"
        width="85%"
        top="5vh"
        @close="closeDetailDialog"
        destroy-on-close
    >
      <!-- 筛选栏：业务类型 + 独立时间范围 -->
      <div class="detail-filter-bar">
        <div class="filter-item">
          <span class="label">业务类型：</span>
          <el-select
              v-model="detailBizType"
              placeholder="全部"
              size="small"
              clearable
              @change="handleDetailBizTypeChange"
              style="width: 140px"
          >
            <el-option label="全部" :value="null"></el-option>
            <el-option label="采购入库" :value="1"></el-option>
            <el-option label="采购出库" :value="2"></el-option>
            <el-option label="采购冲销" :value="4"></el-option>
            <el-option label="期初" :value="5"></el-option>
            <el-option label="支付" :value="6"></el-option>
          </el-select>
        </div>
        <div class="filter-item">
          <span class="label">时间范围：</span>
          <el-date-picker
              size="small"
              v-model="detailTimeRange"
              type="datetimerange"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              value-format="yyyy-MM-dd HH:mm:ss"
              :default-time="['00:00:00', '23:59:59']"
              @change="handleDetailTimeChange"
              :picker-options="pickerOptions"
              style="width: 380px"
          />
        </div>
        <el-button size="small" type="primary" @click="refreshDetailData" icon="el-icon-search">查询</el-button>
      </div>

      <!-- 明细表格 -->
      <el-table
          :data="detailTableData"
          v-loading="detailLoading"
          border
          stripe
          style="width: 100%; margin-top: 16px"
      >
        <el-table-column prop="bizId" label="业务ID" width="100" align="center">
          <template slot-scope="scope">
            {{ scope.row.bizId || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="bizType" label="业务类型" width="110" align="center">
          <template slot-scope="scope">
            {{ getBizTypeText(scope.row.bizType) }}
          </template>
        </el-table-column>
        <el-table-column prop="supplierName" label="供应商名称" min-width="150" align="center">
          <template slot-scope="scope">
            {{ scope.row.supplierName || '-' }}
          </template>
        </el-table-column>
        <el-table-column prop="agoAmt" label="变化前金额(元)" align="right" min-width="140">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.agoAmt) }}
          </template>
        </el-table-column>
        <el-table-column prop="amount" label="变动金额(元)" align="right" min-width="140">
          <template slot-scope="scope">
    <span :style="{ color: getChangeAmount(scope.row) >= 0 ? '#67C23A' : '#F56C6C' }">
      {{ getChangeAmount(scope.row) >= 0 ? '+' : '' }}{{ formatAmount(getChangeAmount(scope.row)) }}
    </span>
          </template>
        </el-table-column>
        <el-table-column prop="aftAmt" label="变化后金额(元)" align="right" min-width="140">
          <template slot-scope="scope">
            {{ formatAmount(scope.row.aftAmt) }}
          </template>
        </el-table-column>
        <el-table-column prop="createId" label="操作人" width="100" align="center">
          <template slot-scope="scope">
            {{ getDoctorName(scope.row.createId) }}
          </template>
        </el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="160" align="center">
          <template slot-scope="scope">
            {{ scope.row.createTime || '-' }}
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页组件 -->
      <div class="detail-pagination" v-if="detailTotal > 0">
        <el-pagination
            @size-change="handleDetailSizeChange"
            @current-change="handleDetailCurrentChange"
            :current-page="detailPageNum"
            :page-sizes="[10, 20, 50, 100]"
            :page-size="detailPageSize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="detailTotal"
        />
      </div>
      <div v-else-if="!detailLoading && detailTableData.length === 0" class="empty-tip">
        <i class="el-icon-info"></i> 暂无变动记录
      </div>
    </el-dialog>
  </div>
</template>

<script>
import dayjs from "dayjs";
import * as echarts from "echarts";
import {supplierStatis, supplierPaidChgPage} from "@/api/SupplierStatis/SupplierStatis";

export default {
  name: "SupplierStatistics",
  data() {
    return {
      loading: false,
      // 主界面时间范围（默认当月）
      timeRange: [
        dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss')
      ],
      // 供应商统计数据列表
      supplierDataList: [],
      // 图表实例
      chartInstance: null,
      showChart: true,
      // 时间选择器快捷选项
      pickerOptions: {
        shortcuts: [
          {
            text: '本月',
            onClick(picker) {
              const start = dayjs().startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '上月',
            onClick(picker) {
              const start = dayjs().subtract(1, 'month').startOf('month').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().subtract(1, 'month').endOf('month').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本季度',
            onClick(picker) {
              const start = dayjs().startOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('quarter').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          },
          {
            text: '本年',
            onClick(picker) {
              const start = dayjs().startOf('year').format('YYYY-MM-DD HH:mm:ss');
              const end = dayjs().endOf('year').format('YYYY-MM-DD HH:mm:ss');
              picker.$emit('pick', [start, end]);
            }
          }
        ]
      },
      // 详情弹窗相关
      detailVisible: false,
      detailSupplierId: null,
      detailSupplierName: '',
      detailLoading: false,
      detailTableData: [],
      detailPageNum: 1,
      detailPageSize: 10,
      detailTotal: 0,
      detailBizType: null,           // 业务类型筛选，null表示全部
      detailTimeRange: []            // 明细弹窗独立的时间范围 [startTime, endTime]
    };
  },
  computed: {
    doctorOptions() {
      return this.$store.getters['preload/getDoctorListOptions'] || [];
    },
    // 表格数据（包含计算字段）
    tableData() {
      return this.supplierDataList.map(item => ({
        ...item,
        endingAmt: (item.beginAmt || 0) + (item.issueInc || 0) - (item.issueReduce || 0)
      }));
    },
    // 总增加金额
    totalInc() {
      return this.supplierDataList.reduce((sum, item) => sum + (item.issueInc || 0), 0);
    },
    // 总减少金额
    totalReduce() {
      return this.supplierDataList.reduce((sum, item) => sum + (item.issueReduce || 0), 0);
    },
    // 总期末金额
    totalEnding() {
      return this.supplierDataList.reduce((sum, item) => {
        const ending = (item.beginAmt || 0) + (item.issueInc || 0) - (item.issueReduce || 0);
        return sum + ending;
      }, 0);
    }
  },
  mounted() {
    this.loadData();
  },
  beforeDestroy() {
    if (this.chartInstance) {
      this.chartInstance.dispose();
      this.chartInstance = null;
    }
  },
  methods: {
    getChangeAmount(row) {
      const aftAmt = row.aftAmt || 0;
      const agoAmt = row.agoAmt || 0;
      return aftAmt - agoAmt;
    },
    getDoctorName(createId) {
      console.log(this.doctorOptions, 'this.doctorOptionsthis.doctorOptions')
      if (!createId) return '-';
      const doctor = this.doctorOptions.find(item => item.id === createId);
      return doctor ? doctor.label : createId;
    },
    // 加载主界面统计数据
    async loadData() {
      this.loading = true;
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const params = {
          clinicId: userInfo.clinicId,
          startTime: this.timeRange[0],
          endTime: this.timeRange[1]
        };

        const res = await supplierStatis(params);
        if (res.code === 200) {
          this.supplierDataList = (res.data || []).map(item => ({
            supplierId: item.supplierId,
            supplierName: item.supplierName,
            beginAmt: item.beginAmt || 0,
            issueInc: item.issueInc || 0,
            issueReduce: item.issueReduce || 0
          }));

          this.$nextTick(() => {
            this.initChart();
          });
        } else {
          this.$message.error(res.message || '获取统计数据失败');
        }
      } catch (error) {
        console.error('加载统计数据失败:', error);
        this.$message.error('系统异常，请联系管理员');
      } finally {
        this.loading = false;
      }
    },

    // 主界面时间变化处理
    handleTimeChange() {
      this.loadData();
    },

    // 格式化金额
    formatAmount(amount) {
      if (amount === null || amount === undefined) return '0.00';
      const num = Number(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },

    // 获取变动趋势百分比
    getTrendPercentage(row) {
      const beginAmt = row.beginAmt || 0;
      const endingAmt = (row.beginAmt || 0) + (row.issueInc || 0) - (row.issueReduce || 0);
      if (beginAmt === 0) return endingAmt > 0 ? 100 : 0;
      const change = ((endingAmt - beginAmt) / beginAmt) * 100;
      return Math.min(Math.abs(Math.round(change)), 100);
    },

    // 获取趋势颜色
    getTrendColor(row) {
      const beginAmt = row.beginAmt || 0;
      const endingAmt = (row.beginAmt || 0) + (row.issueInc || 0) - (row.issueReduce || 0);
      if (endingAmt > beginAmt) return '#67C23A';
      if (endingAmt < beginAmt) return '#F56C6C';
      return '#909399';
    },

    // 获取趋势文本
    getTrendText(row) {
      const beginAmt = row.beginAmt || 0;
      const endingAmt = (row.beginAmt || 0) + (row.issueInc || 0) - (row.issueReduce || 0);
      const change = endingAmt - beginAmt;
      if (change > 0) return `↑ +${this.formatAmount(change)}`;
      if (change < 0) return `↓ ${this.formatAmount(change)}`;
      return '持平';
    },

    // 导出报表
    handleExport() {
      if (this.supplierDataList.length === 0) {
        this.$message.warning('暂无数据可导出');
        return;
      }

      const exportRows = [];

      exportRows.push({
        '统计项': '统计开始时间',
        '供应商名称': '',
        '期初金额': '',
        '本期增加': '',
        '本期减少': '',
        '期末金额': '',
        '变动趋势': this.timeRange[0]
      });
      exportRows.push({
        '统计项': '统计结束时间',
        '供应商名称': '',
        '期初金额': '',
        '本期增加': '',
        '本期减少': '',
        '期末金额': '',
        '变动趋势': this.timeRange[1]
      });
      exportRows.push({
        '统计项': '---',
        '供应商名称': '---',
        '期初金额': '---',
        '本期增加': '---',
        '本期减少': '---',
        '期末金额': '---',
        '变动趋势': '---'
      });

      this.tableData.forEach(supplier => {
        const endingAmt = (supplier.beginAmt || 0) + (supplier.issueInc || 0) - (supplier.issueReduce || 0);
        const change = endingAmt - (supplier.beginAmt || 0);
        const trendText = change > 0 ? `增加 ${this.formatAmount(change)}` : (change < 0 ? `减少 ${this.formatAmount(Math.abs(change))}` : '持平');

        exportRows.push({
          '统计项': '供应商数据',
          '供应商名称': supplier.supplierName,
          '期初金额': this.formatAmount(supplier.beginAmt) + '元',
          '本期增加': this.formatAmount(supplier.issueInc) + '元',
          '本期减少': this.formatAmount(supplier.issueReduce) + '元',
          '期末金额': this.formatAmount(endingAmt) + '元',
          '变动趋势': trendText
        });
      });

      exportRows.push({
        '统计项': '---',
        '供应商名称': '---',
        '期初金额': '---',
        '本期增加': '---',
        '本期减少': '---',
        '期末金额': '---',
        '变动趋势': '---'
      });
      exportRows.push({
        '统计项': '汇总统计',
        '供应商名称': `共 ${this.supplierDataList.length} 家供应商`,
        '期初金额': '',
        '本期增加': `总计: ${this.formatAmount(this.totalInc)}元`,
        '本期减少': `总计: ${this.formatAmount(this.totalReduce)}元`,
        '期末金额': `总计: ${this.formatAmount(this.totalEnding)}元`,
        '变动趋势': ''
      });

      const headers = Object.keys(exportRows[0]);
      const csvRows = [
        headers.join(','),
        ...exportRows.map(row => headers.map(header => `"${row[header] || ''}"`).join(','))
      ];
      const csvContent = csvRows.join('\n');

      const blob = new Blob(['\uFEFF' + csvContent], {type: 'text/csv;charset=utf-8;'});
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.href = url;
      link.setAttribute('download', `供应商统计报表_${dayjs().format('YYYYMMDDHHmmss')}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      this.$message.success('导出成功');
    },

    // 初始化图表
    initChart() {
      if (!this.$refs.chartDom || this.supplierDataList.length === 0) return;

      if (this.chartInstance) {
        this.chartInstance.dispose();
      }

      this.chartInstance = echarts.init(this.$refs.chartDom);

      const supplierNames = this.supplierDataList.map(item => item.supplierName);
      const beginAmtData = this.supplierDataList.map(item => item.beginAmt || 0);
      const issueIncData = this.supplierDataList.map(item => item.issueInc || 0);
      const issueReduceData = this.supplierDataList.map(item => item.issueReduce || 0);
      const endingAmtData = this.supplierDataList.map(item => {
        return (item.beginAmt || 0) + (item.issueInc || 0) - (item.issueReduce || 0);
      });

      const option = {
        tooltip: {
          trigger: 'axis',
          axisPointer: {type: 'shadow'},
          formatter: (params) => {
            let result = params[0].axisValue + '<br/>';
            params.forEach(item => {
              result += `${item.marker} ${item.seriesName}: ${this.formatAmount(item.value)} 元<br/>`;
            });
            return result;
          }
        },
        legend: {
          data: ['期初金额', '本期增加', '本期减少', '期末金额'],
          top: 0,
          right: 0,
          itemWidth: 30,
          itemHeight: 14
        },
        grid: {
          left: '8%',
          right: '5%',
          top: '12%',
          bottom: '8%',
          containLabel: true
        },
        xAxis: {
          type: 'category',
          data: supplierNames,
          axisLabel: {
            fontSize: 12,
            rotate: supplierNames.length > 4 ? 25 : 0,
            interval: 0
          },
          axisLine: {lineStyle: {color: '#999'}}
        },
        yAxis: {
          type: 'value',
          name: '金额（元）',
          nameTextStyle: {fontSize: 12},
          axisLabel: {
            fontSize: 11,
            formatter: (value) => {
              if (value >= 10000) return (value / 10000).toFixed(1) + '万';
              return value;
            }
          },
          splitLine: {lineStyle: {type: 'dashed'}}
        },
        series: [
          {
            name: '期初金额',
            type: 'bar',
            data: beginAmtData,
            itemStyle: {
              color: '#409EFF',
              borderRadius: [4, 4, 0, 0],
              label: {
                show: true,
                position: 'top',
                formatter: (params) => this.formatAmount(params.value),
                fontSize: 10
              }
            },
            barWidth: '18%'
          },
          {
            name: '本期增加',
            type: 'bar',
            data: issueIncData,
            itemStyle: {
              color: '#67C23A',
              borderRadius: [4, 4, 0, 0],
              label: {
                show: true,
                position: 'top',
                formatter: (params) => this.formatAmount(params.value),
                fontSize: 10
              }
            },
            barWidth: '18%'
          },
          {
            name: '本期减少',
            type: 'bar',
            data: issueReduceData,
            itemStyle: {
              color: '#F56C6C',
              borderRadius: [4, 4, 0, 0],
              label: {
                show: true,
                position: 'top',
                formatter: (params) => this.formatAmount(params.value),
                fontSize: 10
              }
            },
            barWidth: '18%'
          },
          {
            name: '期末金额',
            type: 'bar',
            data: endingAmtData,
            itemStyle: {
              color: '#E6A23C',
              borderRadius: [4, 4, 0, 0],
              label: {
                show: true,
                position: 'top',
                formatter: (params) => this.formatAmount(params.value),
                fontSize: 10
              }
            },
            barWidth: '18%'
          }
        ]
      };

      this.chartInstance.setOption(option);
      window.addEventListener('resize', this.handleChartResize);
    },

    handleChartResize() {
      if (this.chartInstance) this.chartInstance.resize();
    },

    // ========== 供应商详情相关方法 ==========
    // 显示详情弹窗
    showDetail(row) {
      this.detailSupplierId = row.supplierId;
      this.detailSupplierName = row.supplierName;
      this.detailPageNum = 1;
      this.detailBizType = null;
      // 复制主界面的当前时间范围作为默认值
      this.detailTimeRange = [...this.timeRange];
      this.detailVisible = true;
      this.loadDetailData();
    },

    // 加载供应商资金变动明细（使用 detailTimeRange）
    async loadDetailData() {
      if (!this.detailSupplierId) return;
      if (!this.detailTimeRange || this.detailTimeRange.length !== 2) {
        this.$message.warning('请选择完整的时间范围');
        return;
      }
      this.detailLoading = true;
      try {
        const userInfo = JSON.parse(localStorage.getItem('userInfo') || '{}');
        const params = {
          supplierId: this.detailSupplierId,
          startTime: this.detailTimeRange[0],
          endTime: this.detailTimeRange[1],
          pageNum: this.detailPageNum,
          pageSize: this.detailPageSize
        };
        if (this.detailBizType !== null && this.detailBizType !== '') {
          params.bizType = this.detailBizType;
        }

        const res = await supplierPaidChgPage(params);
        if (res.code === 200 && res.data) {
          this.detailTableData = res.data.content || [];
          this.detailTotal = res.data.totalSize || 0;
        } else {
          this.$message.error(res.message || '获取明细数据失败');
          this.detailTableData = [];
          this.detailTotal = 0;
        }
      } catch (error) {
        console.error('加载供应商明细失败:', error);
        this.$message.error('系统异常，请联系管理员');
        this.detailTableData = [];
        this.detailTotal = 0;
      } finally {
        this.detailLoading = false;
      }
    },

    // 明细弹窗内时间范围变化处理
    handleDetailTimeChange() {
      this.detailPageNum = 1;
      this.loadDetailData();
    },

    // 业务类型筛选变更
    handleDetailBizTypeChange() {
      this.detailPageNum = 1;
      this.loadDetailData();
    },

    // 刷新明细数据（保持当前筛选和分页）
    refreshDetailData() {
      this.loadDetailData();
    },

    // 分页大小变化
    handleDetailSizeChange(size) {
      this.detailPageSize = size;
      this.detailPageNum = 1;
      this.loadDetailData();
    },

    // 当前页码变化
    handleDetailCurrentChange(page) {
      this.detailPageNum = page;
      this.loadDetailData();
    },

    // 关闭弹窗时的清理工作
    closeDetailDialog() {
      this.detailSupplierId = null;
      this.detailSupplierName = '';
      this.detailTableData = [];
      this.detailTotal = 0;
      this.detailPageNum = 1;
      this.detailPageSize = 10;
      this.detailBizType = null;
      this.detailTimeRange = [];
    },

    // 业务类型数字转文本
    getBizTypeText(bizType) {
      const map = {
        1: '采购入库',
        2: '采购出库',
        4: '采购冲销',
        5: '期初',
        6: '支付'
      };
      return map[bizType] || `未知(${bizType})`;
    }
  },
  watch: {
    supplierDataList: {
      handler() {
        this.$nextTick(() => {
          this.initChart();
        });
      },
      deep: true
    }
  }
};
</script>

<style scoped lang="less">
.supplier-statistics-container {
  padding: 20px;
  background: #f5f7fa;
  min-height: 100%;

  .statistics-card {
    background: #fff;
    border-radius: 12px;
    box-shadow: 0 2px 12px rgba(0, 0, 0, 0.1);
    overflow: hidden;

    .card-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 16px 24px;
      background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
      color: #fff;

      .header-title {
        display: flex;
        align-items: center;
        font-size: 18px;
        font-weight: 600;

        i {
          font-size: 24px;
          margin-right: 10px;
        }
      }

      .header-toolbar {
        display: flex;
        gap: 12px;
        align-items: center;
      }
    }

    .summary-cards {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 20px;
      padding: 24px 24px 0 24px;

      .summary-card {
        display: flex;
        align-items: center;
        padding: 20px;
        background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
        border-radius: 12px;
        border: 1px solid #ebeef5;
        transition: all 0.3s ease;

        &:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }

        .summary-icon {
          width: 50px;
          height: 50px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 10px;
          margin-right: 16px;
          font-size: 24px;

          &.total-icon {
            background: linear-gradient(135deg, #e6f7ff, #bae7ff);
            color: #409EFF;
          }

          &.inc-icon {
            background: linear-gradient(135deg, #f6ffed, #d9f7be);
            color: #67C23A;
          }

          &.reduce-icon {
            background: linear-gradient(135deg, #fff2f0, #ffccc7);
            color: #F56C6C;
          }

          &.ending-icon {
            background: linear-gradient(135deg, #fff7e6, #ffe7ba);
            color: #E6A23C;
          }
        }

        .summary-info {
          flex: 1;

          .summary-label {
            font-size: 13px;
            color: #909399;
            margin-bottom: 6px;
          }

          .summary-value {
            font-size: 22px;
            font-weight: bold;
            color: #303133;

            .unit {
              font-size: 12px;
              font-weight: normal;
              margin-left: 4px;
              color: #909399;
            }
          }
        }
      }
    }

    .data-table-wrapper {
      padding: 24px;

      .supplier-name-cell {
        display: flex;
        align-items: center;
        gap: 8px;

        i {
          font-size: 18px;
          color: #409EFF;
        }

        span {
          font-weight: 500;
        }
      }

      .amount-text {
        font-family: 'Monaco', 'Menlo', monospace;
        font-weight: 500;

        &.begin-amount {
          color: #409EFF;
        }

        &.inc-amount {
          color: #67C23A;
        }

        &.reduce-amount {
          color: #F56C6C;
        }

        &.ending-amount {
          color: #E6A23C;
          font-weight: bold;
        }
      }

      .trend-indicator {
        display: flex;
        flex-direction: column;
        gap: 6px;

        .trend-text {
          font-size: 12px;
          font-weight: 500;
        }
      }
    }

    .chart-container {
      padding: 0 24px 24px 24px;

      .chart-header {
        font-size: 16px;
        font-weight: 600;
        color: #303133;
        margin-bottom: 16px;
        padding-bottom: 12px;
        border-bottom: 1px solid #ebeef5;

        i {
          margin-right: 6px;
          color: #667eea;
        }
      }
    }
  }
}

// 详情模态框样式
.detail-filter-bar {
  display: flex;
  align-items: center;
  gap: 20px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e4e7ed;
  flex-wrap: wrap;

  .filter-item {
    display: flex;
    align-items: center;
    gap: 8px;

    .label {
      font-size: 13px;
      color: #606266;
      font-weight: 500;
    }
  }
}

.detail-pagination {
  margin-top: 20px;
  display: flex;
  justify-content: flex-end;
}

.empty-tip {
  text-align: center;
  color: #909399;
  padding: 40px 0;
  font-size: 14px;

  i {
    font-size: 24px;
    margin-right: 6px;
    vertical-align: middle;
  }
}

// 表格样式优化
::v-deep .el-table {
  border-radius: 8px;
  overflow: hidden;

  th {
    background-color: #f5f7fa !important;
  }

  .el-table__row {
    transition: background-color 0.2s;

    &:hover {
      background-color: #f0f9ff !important;
    }
  }
}

::v-deep .el-progress-bar__inner {
  border-radius: 4px;
}

@media (max-width: 1200px) {
  .supplier-statistics-container .statistics-card .summary-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .supplier-statistics-container {
    padding: 12px;

    .statistics-card .card-header {
      flex-direction: column;
      gap: 12px;

      .header-toolbar {
        width: 100%;
        justify-content: center;
        flex-wrap: wrap;
      }
    }

    .statistics-card .summary-cards {
      grid-template-columns: 1fr;
      gap: 12px;
      padding: 16px 16px 0 16px;
    }

    .statistics-card .data-table-wrapper {
      padding: 16px;
      overflow-x: auto;
    }
  }
}
</style>